import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Zap, XCircle } from "lucide-react";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import logo from "@/assets/logo.png";

/** Called when a customer scans a per-station QR sticker.
 * If they're signed in, the interceptor adds the Authorization header
 * and the backend attributes the session to their user. Otherwise,
 * they enter their email (matches a pre-registered guest slot). */
export default function StationCheckin() {
  const { stationId } = useParams();
  const [sp] = useSearchParams();
  const qrKey = sp.get("k") || null;
  const [phase, setPhase] = useState("choose"); // choose | email | working | ok | err
  const [msg, setMsg] = useState("");
  const [bookings, setBookings] = useState([]);
  const [pickedBooking, setPickedBooking] = useState(null);
  const [email, setEmail] = useState("");
  const [session, setSession] = useState(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : "";
  const authed = !!token;

  useEffect(() => {
    if (!authed) return;
    // Load user's active bookings
    (async () => {
      try {
        const r = await apiClient.get("/bookings/my");
        const now = new Date();
        const soon = new Date(now.getTime() + 8 * 3600 * 1000);
        const active = (r.data || []).filter((b) => {
          if (b.status !== "confirmed" && b.status !== "completed") return false;
          const s = new Date(b.start_time), e = new Date(b.end_time);
          return e >= new Date(now.getTime() - 15 * 60 * 1000) && s <= soon && (b.station_ids || []).includes(stationId);
        });
        setBookings(active);
        if (active.length === 1) setPickedBooking(active[0]);
      } catch (e) { setMsg(getErrorMessage(e, "Failed to load bookings")); }
    })();
  }, [authed, stationId]);

  const doCheckin = async (bookingId, guestEmail) => {
    setPhase("working");
    try {
      const r = await apiClient.post(`/checkin/station/${stationId}`, { booking_id: bookingId, email: guestEmail || null, k: qrKey });
      setSession(r.data.session);
      setPhase("ok");
    } catch (e) {
      setMsg(getErrorMessage(e, "Check-in failed"));
      setPhase("err");
    }
  };

  const guestSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    // No booking chosen for the guest — backend must find a booking they're on.
    // We reuse central check-in to find guest bookings for this email.
    try {
      const cfg = await apiClient.get("/qr/central/token", { params: { display_token: "not-a-token" } }).catch(() => null);
      // Fallback path: hit central endpoint to pull their bookings; but we can't
      // without a display_token. Simplest: guest must ask staff / use reception QR first.
      // As an MVP, prompt them to check in at reception first.
    } catch { /* noop */ }
    setMsg("Please check in at reception first — the front desk will link your email to your booking.");
    setPhase("err");
  };

  return (
    <div className="min-h-screen grid-overlay flex items-center justify-center px-4 py-6">
      <div className="w-full max-w-md glass rounded-3xl p-8 text-center">
        <img src={logo} alt="Pit Lane" className="h-12 w-auto object-contain mx-auto mb-4" />

        {phase === "choose" && authed && (
          <>
            <h1 className="font-display text-2xl font-bold text-white mb-2">Start your session</h1>
            {bookings.length === 0 ? (
              <>
                <p className="text-slate-400 mb-4">We didn't find an active booking on this station for your account.</p>
                <p className="text-slate-500 text-sm">Ask the front desk to help you check in.</p>
              </>
            ) : (
              <>
                <p className="text-slate-400 mb-4 text-sm">Pick your booking:</p>
                <div className="space-y-2">
                  {bookings.map((b) => (
                    <button key={b.id} onClick={() => doCheckin(b.id)}
                      data-testid={`station-book-${b.id}`}
                      className="w-full text-left p-3 rounded-xl bg-black/30 border border-white/10 hover:border-pink-500/50">
                      <div className="text-white text-sm font-semibold">{new Date(b.start_time).toLocaleString()}</div>
                      <div className="text-slate-400 text-xs">{(b.station_ids || []).length} station(s)</div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {phase === "choose" && !authed && (
          <>
            <h1 className="font-display text-2xl font-bold text-white mb-2">Please check in at reception</h1>
            <p className="text-slate-400 text-sm">Scan the reception QR (or sign in) to link this station to your booking.</p>
          </>
        )}

        {phase === "working" && (
          <>
            <Loader2 className="w-16 h-16 mx-auto animate-spin mb-4 text-cyan-400" />
            <div className="text-white">Opening your session…</div>
          </>
        )}

        {phase === "ok" && (
          <>
            <CheckCircle2 className="w-20 h-20 mx-auto text-emerald-400 mb-3" data-testid="station-checkin-ok" />
            <h1 className="font-display text-3xl font-bold text-white mb-2">You're in!</h1>
            <p className="text-slate-300 mb-1">Station: <b>{session?.station_name}</b></p>
            <p className="text-slate-500 text-sm flex items-center justify-center gap-1"><Zap className="w-4 h-4 text-emerald-400" /> Session started</p>
          </>
        )}

        {phase === "err" && (
          <>
            <XCircle className="w-16 h-16 mx-auto text-red-400 mb-3" data-testid="station-checkin-err" />
            <h1 className="font-display text-xl font-bold text-white mb-2">Check-in failed</h1>
            <p className="text-slate-400 text-sm">{msg}</p>
          </>
        )}
      </div>
    </div>
  );
}
